import { useState } from "react";
import { fmt, type CalcResult, type Scenario } from "@h2lca/core";
import { Button, ButtonLink, SelectField } from "@/components/ui/FormControls";
import { LicensedAction } from "@/components/licensing/LicensedAction";
import { api } from "@/lib/api";
import { Block } from "@/components/workshop/Section";

interface Project { id: string; name: string; siteName: string }

/** 미리보기에서 선택한 프로젝트에 서버 산정 결과를 실제로 저장한다. */
export function RunSavePanel({ scenario }: { scenario: Scenario }) {
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ id: string; projectId: string; projectName: string; perKgH2: number | null } | null>(null);

  async function chooseProject() {
    setOpen(true); setBusy(true); setError(null); setSaved(null);
    try {
      const response = await api<{ projects: Project[] }>("/workspace");
      setProjects(response.projects);
      setProjectId(response.projects[0]?.id ?? "");
    } catch (e) { setError(e instanceof Error ? e.message : "프로젝트를 불러오지 못했습니다."); }
    finally { setBusy(false); }
  }
  async function save() {
    setBusy(true); setError(null);
    try {
      const response = await api<{ run: { id: string }; result: CalcResult }>(`/workspace/projects/${projectId}/runs`, { method: "POST", json: { scenario } });
      setSaved({ id: response.run.id, projectId, projectName: projects.find((p) => p.id === projectId)?.name ?? "", perKgH2: response.result.perKgH2 });
    } catch (e) { setError(e instanceof Error ? e.message : "실행 기록을 저장하지 못했습니다."); }
    finally { setBusy(false); }
  }
  return (
    <Block title="산정 실행 기록" aside={<LicensedAction feature="execution_history" label="실행 기록 저장" title="실행 기록 저장" description="입력과 마스터 데이터 스냅샷을 프로젝트에 보관하는 기업용 기능입니다." onAvailable={() => void chooseProject()} />}>
      <p className="text-footnote text-ink-subtle">저장할 프로젝트를 선택하고 저장을 누르면, 현재 입력과 서버의 마스터 데이터로 산정한 결과를 실행 기록으로 보관합니다.</p>
      {open && !saved && <div className="flex flex-col gap-content">
        {busy && <p role="status" className="text-footnote">처리 중…</p>}
        {projects.length > 0 && <SelectField label="저장할 프로젝트" value={projectId} disabled={busy} onChange={(e) => setProjectId(e.target.value)}>{projects.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.siteName}</option>)}</SelectField>}
        <div className="flex flex-wrap gap-item"><Button variant="primary" disabled={busy || !projectId} onClick={() => void save()}>선택한 프로젝트에 저장</Button><Button disabled={busy} onClick={() => setOpen(false)}>취소</Button><ButtonLink to="/workspace">프로젝트 관리</ButtonLink></div>
      </div>}
      {error && <p role="alert" className="note note-danger text-footnote">{error}</p>}
      {saved && <div className="flex flex-col gap-item"><p role="status" className="note note-ok text-footnote">{saved.projectName}에 실행 기록 {saved.id}을 저장했습니다. {saved.perKgH2 === null ? "계산 제한 상태도 함께 기록되었습니다." : `1 kg당 ${fmt(saved.perKgH2, 3)} kgCO2eq/kgH2입니다.`}</p><div><ButtonLink to={`/workspace/projects/${saved.projectId}`}>저장된 실행 기록 확인</ButtonLink></div></div>}
    </Block>
  );
}
